import { useState } from 'react';
import { useWorkerStore } from '@/store/workerStore';
import { WorkerFormData } from '@/lib/validation/workerSchema';
import { useToast } from './useToast';
import { supabase } from '@/lib/supabase/client';
import { Worker, WorkerStatus } from '@/types/workers';

const WORKER_TEMP_PASSWORD = 'Worker@123';

export const useWorkers = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { deleteWorker: deleteWorkerInStore, workers, fetchWorkers } = useWorkerStore();
  const { toast } = useToast();

  const createWorker = async (data: WorkerFormData) => {
    try {
      setIsLoading(true);
      
      // Get current session for authorization
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        throw new Error('You must be logged in to create workers');
      }

      // Call the Edge Function to create user (uses admin API, doesn't affect current session)
      const response = await supabase.functions.invoke('create-user', {
        body: {
          email: data.email,
          password: WORKER_TEMP_PASSWORD,
          role: 'worker',
          full_name: data.full_name,
          mobile_number: data.mobile_number || null,
        },
      });

      // Check response.data.error first — the edge function returns detailed error JSON
      // response.error is a generic FunctionsHttpError ("non-2xx status code") that masks the real message
      if (response.data?.error) {
        throw new Error(response.data.error);
      }

      if (response.error) {
        throw new Error(response.error.message || 'Failed to create worker');
      }

      const userId = response.data?.user?.id;

      // Wait for the trigger to complete
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Update additional user data (department)
      if (userId && data.department_id) {
        const { error: updateError } = await supabase
          .from('users')
          .update({
            department_id: data.department_id,
            updated_at: new Date().toISOString()
          })
          .eq('id', userId);

        if (updateError) {
          console.error('Failed to update department:', updateError);
        }
      }

      // Refresh the workers list
      await fetchWorkers();

      return { 
        success: true, 
        user: response.data?.user,
        tempPassword: WORKER_TEMP_PASSWORD 
      };
    } catch (error) {
      console.error('Error creating worker:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const updateWorker = async (workerId: string, data: Partial<WorkerFormData>) => {
    try {
      setIsLoading(true);

      // Update user profile in public.users table
      const { error: updateError } = await supabase
        .from('users')
        .update({
          full_name: data.full_name,
          mobile_number: data.mobile_number,
          department_id: data.department_id,
          status: data.status as WorkerStatus,
          updated_at: new Date().toISOString()
        })
        .eq('id', workerId);

      if (updateError) throw updateError;

      // Refresh workers list
      await fetchWorkers();
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to update worker",
        variant: "destructive"
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const deleteWorker = async (workerId: string) => {
    try {
      setIsLoading(true);
      
      // Update status to inactive (soft delete)
      const { error: updateError } = await supabase
        .from('users')
        .update({ 
          status: 'inactive',
          updated_at: new Date().toISOString()
        })
        .eq('id', workerId)
        .eq('role', 'worker');

      if (updateError) throw updateError;

      // No longer attempting to delete the auth user to prevent conflicts
      // Just update the store to reflect the inactive status
      await deleteWorkerInStore(workerId);
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to delete worker",
        variant: "destructive"
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    createWorker,
    updateWorker,
    deleteWorker,
    isLoading,
    // Expose workers list - each worker has the full_name property for the UI
    workers: workers as unknown as Worker[],
    // Add the ability to refresh the workers list
    fetchWorkers
  };
};
